import React from "react";
import { lighten } from "polished";
import cns from "classnames";
import { Card, Divider, Grid, IconButton } from "@material-ui/core";
import {
  EditRounded,
  AddPhotoAlternateRounded,
  StarBorder,
  StarRounded
} from "@material-ui/icons";
import { SearchEvent } from "@containers/EventLog/SearchEvent/SearchEvent";
import Body from "@components/typography/Body";
import Title from "@components/typography/Title";
import { ThemeType } from "@theme";
import locale from "@bonliva-crm/locale";
import { EventLog_EventFragment as Event } from "@graphql";

import ResponsibleSection from "./ResponsibleSection";
import { makeStyles } from "@material-ui/core/styles";
import { ItemCardAvatar, AvatarProps } from "./ItemCardAvatar";

export type ItemCardProps = {
  entity: {
    _id?: string;
    Name?: string;
    Events?: Event[];
    ResponsiblePerson?: { Name: string; PersonId: string };
  } & Record<string, any>;

  flat?: boolean;
  /** first priority */
  title?: React.ReactNode;
  selected?: boolean;
  /** second priority */
  subheading?: React.ReactNode;
  /** third priority */
  subTitle?: React.ReactNode;
  chips?: React.ReactNode;
  keyValues?: Array<{ key: string; value: React.ReactNode }>;
  children?: React.ReactNode;
  highlightSearch?: string;
  rightControls?: React.ReactNode;
  details?: React.ReactNode;
  withRightPane?: React.ReactNode;
  onAddBgClick?: (e: React.SyntheticEvent<any>) => void;
  onClick?: (e: React.SyntheticEvent<any>) => void;
  onEdit?: (e: React.SyntheticEvent<any>) => void;
  onResponsiblePersonClick?: (e: any) => void;
  onStarClick?: (e: React.SyntheticEvent<any>) => void;
  onTitleClick?: (e: React.SyntheticEvent<any>) => void;
  responsiblePerson?: boolean;
  renderTitle?: () => React.ReactNode;
  error?: boolean;
  showStar?: null | "hollow" | "full";
  style?: React.CSSProperties;
  avatarColor?: string;
} & AvatarProps;

const ItemCard = (props: ItemCardProps) => {
  const { flat, onClick, selected, error } = props;
  const classes = useStyles({});
  const cardClasses = cns(classes.root, {
    [classes.flat]: Boolean(flat),
    [classes.clickable]: Boolean(onClick),
    [classes.error]: error,
    [classes.selected]: selected
  });

  return (
    <Card
      className={cardClasses}
      elevation={props.flat ? 0 : 2}
      onClick={(e) => {
        if (window.getSelection().toString()) {
          return;
        }

        const elem = e.target as HTMLElement;

        if (elem.tagName !== "A") {
          onClick && onClick(e);
        }
      }}
    >
      <ItemInfo {...props} />
    </Card>
  );
};

export const ItemInfo = (props: ItemCardProps) => {
  const {
    withRightPane,
    details,
    entity = {},
    keyValues,
    onAddBgClick,
    onEdit,
    onResponsiblePersonClick,
    onStarClick,
    subheading,
    subTitle,
    onTitleClick,
    responsiblePerson,
    showStar,
    style
  } = props;
  const classes = useStyles({});

  const Star =
    showStar === "full" ? StarRounded : showStar === "hollow" ? StarBorder : null;

  const title = props.renderTitle ? (
    props.renderTitle()
  ) : (
    <Title onClick={onTitleClick} className={cns(classes.title, { highlighter: true })}>
      {props.title || entity.Name || locale.missing}
    </Title>
  );

  return (
    <>
      {onAddBgClick && (
        <IconButton className={classes.addBgBtn} onClick={onAddBgClick}>
          <AddPhotoAlternateRounded fontSize="small" />
        </IconButton>
      )}

      <Grid container item spacing={0} className={classes.grid} style={style}>
        <Grid item xs={withRightPane ? 6 : 12}>
          <div className={classes.infoCell}>
            <div className={classes.header}>
              <ItemCardAvatar {...props} />
              <div className={classes.headerText}>
                <div className={classes.nameMarksWrapper}>
                  {title}
                  <div
                    className={classes.chips}
                    style={{ display: "flex", alignItems: "center" }}
                  >
                    {props.chips}
                  </div>
                  {Star && (
                    <Star
                      className={classes.star}
                      onClick={onStarClick}
                      color="primary"
                    />
                  )}
                </div>

                {subheading && <div className={classes.subheading}>{subheading}</div>}

                {subTitle && typeof subTitle === "string" && (
                  <Body>
                    <strong>{subTitle}</strong>
                  </Body>
                )}

                {subTitle && typeof subTitle !== "string" && subTitle}

                {keyValues &&
                  keyValues
                    .filter((x) => Boolean(x.key) && Boolean(x.value))
                    .map((x, i) => (
                      <Body key={i} className="highlighter">
                        <strong>{x.key}</strong> {x.value}
                      </Body>
                    ))}
                {props.children}
              </div>
              <div className={classes.rightControls}>
                {responsiblePerson && entity.ResponsiblePerson && (
                  <ResponsibleSection
                    name={entity.ResponsiblePerson.Name}
                    onClick={onResponsiblePersonClick}
                  />
                )}
                {onEdit && (
                  <IconButton
                    className={classes.editButton}
                    onClick={(e) => {
                      e.stopPropagation();
                      onEdit && onEdit(e);
                    }}
                  >
                    <EditRounded />
                  </IconButton>
                )}

                {props.rightControls}
              </div>
            </div>

            {details && <Divider />}
            {details}
          </div>
        </Grid>
        {withRightPane ? (
          <Grid item xs={6}>
            <div className={classes.infoCell}>{withRightPane}</div>
          </Grid>
        ) : null}
      </Grid>

      {entity.Events && (
        <div className={classes.events}>
          {entity.Events.sortBy((x) => x.CreatedOn).map((ev) => {
            return <SearchEvent key={ev._id} item={ev} />;
          })}
        </div>
      )}
    </>
  );
};

const useStyles = makeStyles((theme: ThemeType) => ({
  root: {
    overflow: "visible",
    position: "relative",
    transition: theme.transitions.create("all"),

    "& .highlight": {
      backgroundColor: lighten(0.2, theme.palette.secondary.main),
      fontWeight: "inherit"
    }
  },

  flat: {
    // border: `1px solid ${theme.palette.grey[300]}`,
    backgroundColor: "transparent",
    boxShadow: "none",
    padding: 0,

    "&:hover": {
      boxShadow: "none",
      backgroundColor: "transparent"
    }
  },

  addBgBtn: {
    position: "absolute",
    left: 6,
    bottom: 6,
    padding: 0
  },

  error: {
    backgroundColor: lighten(0.15, theme.palette.primary.main)
  },

  title: {
    cursor: "pointer !important"
  },

  subheading: {
    "& *": {
      cursor: "pointer !important",
      textDecoration: "none"
    }
  },

  clickable: {
    cursor: "pointer",
    "&:hover, &:active": {
      boxShadow: theme.shadows[4]
    }
  },

  grid: {
    display: "flex",

    "&:after": {
      ...theme.mixins.fullAbsolute,
      backgroundRepeat: "no-repeat"
    }
  },

  infoCell: {
    flex: 1
  },

  rightControls: {
    marginLeft: "auto",
    flexDirection: "row",
    display: "flex"
  },

  editButton: {
    "&:hover, &:active": {
      color: theme.palette.common.black,
      transition: theme.transitions.create("all")
    }
  },

  location: {
    marginBottom: theme.spacing(1 / 2)
  },

  selected: {
    borderLeft: `${theme.spacing(2)}px solid ${theme.palette.primary.main}`,
    borderRight: `${theme.spacing(2)}px solid ${theme.palette.primary.main}`
  },

  header: {
    display: "flex",
    alignItems: "flex-start",
    padding: theme.spacing(2)
  },

  headerText: {
    display: "flex",
    flexDirection: "column",
    marginLeft: 10
  },

  nameMarksWrapper: {
    alignItems: "center",
    display: "flex"
  },

  warning: {
    alignItems: "center",
    display: "flex",
    marginLeft: theme.spacing(1 / 2)
  },

  events: {
    borderTop: `1px solid ${theme.palette.divider}`
  },

  chips: {
    marginLeft: theme.spacing(1 / 2)
  },

  star: {
    marginLeft: theme.spacing(1 / 2),

    "&:hover": {
      filter: "drop-shadow(0px 0px 1px rgba(0,0,0,0.6))",
      transition: theme.transitions.create("filter", { duration: 150 })
    }
  }
}));

export default ItemCard;
